// Was this document actually read? — the report's copy of backend/read-quality.js.
//
// Mirrored, not shared, because the backend deploys without the frontend tree. The two
// must give the same verdict and the same words: backend/verify-read-quality.js
// requires this file and holds it to the backend's answers.
//
// NOT READ = the parser skipped the document, or it read zero entries while the
// document prints a total above $0 (GAP-089, 2026-10-29; until then the total had to
// be at least $1,000, so a zero-row parse of a $600 estimate showed "Clean Estimate").
// Computed from the parse itself rather than trusted from parsedData.readQuality, so
// an audit saved under the old threshold reopens with the current verdict.

const TM_SCHEDULE_ARRAYS = ['schedAWorkers', 'schedBItems', 'equipmentItems', 'schedDVendors', 'schedEItems'];

function countTmScheduleEntries(parsedData) {
    if (!parsedData) return null;
    let applicable = false;
    let total = 0;
    for (const key of TM_SCHEDULE_ARRAYS) {
        const arr = parsedData[key];
        if (Array.isArray(arr)) { applicable = true; total += arr.length; }
    }
    return applicable ? total : null;
}

const money = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[$,]/g, ''));
    return Number.isFinite(n) ? n : 0;
};

function printedTotal(parsedData) {
    if (!parsedData) return 0;
    const s = parsedData.summary || {};
    const candidates = [parsedData.grandTotal, parsedData.invoiceGrandTotal, s.rcv, s.lineItemTotal,
        parsedData.lineItemTotals && parsedData.lineItemTotals.total];
    for (const c of candidates) {
        const n = money(c);
        if (n > 0) return n;
    }
    return 0;
}

function layoutIdentified(parsedData) {
    if (!parsedData || parsedData.skipAudit || !Array.isArray(parsedData.allLineItems)) return null;
    if (parsedData.columnFormatMatchedBy) return true;
    if (parsedData.columnFormatFallback === true) return false;
    return null;
}

function computeReadQuality(parsedData) {
    const xactLineItems = Array.isArray(parsedData && parsedData.allLineItems) ? parsedData.allLineItems.length : null;
    const tmEntries = xactLineItems === null ? countTmScheduleEntries(parsedData) : null;
    const li = xactLineItems !== null ? xactLineItems : tmEntries;
    const total = printedTotal(parsedData);
    let underRead = false, reason = null;
    if (parsedData && parsedData.skipAudit) {
        underRead = true; reason = 'parser_skipped';
    } else if (xactLineItems === 0 && total > 0) {
        underRead = true; reason = 'zero_line_items_with_total';
    } else if (tmEntries === 0 && total > 0) {
        underRead = true; reason = 'zero_tm_entries_with_total';
    }
    return { underRead, reason, lineItems: li, grandTotal: total || null, layoutIdentified: layoutIdentified(parsedData) };
}

function notReadCopy(rq) {
    if (!rq || !rq.underRead) return null;
    let lead;
    if (rq.reason === 'parser_skipped') {
        lead = 'Audee treated this document as a carrier payment summary with no line items, so nothing was audited.';
    } else if (rq.layoutIdentified === true) {
        lead = "Audee found this estimate's line-item table but could not read its rows.";
    } else if (rq.layoutIdentified === false) {
        lead = "Audee could not identify this estimate's line-item table layout, so it could not read its rows.";
    } else {
        lead = "Audee could not read this estimate's line items.";
    }
    const noun = rq.reason === 'zero_tm_entries_with_total' ? 'entries' : 'line items';
    const totalLine = rq.reason !== 'parser_skipped' && rq.grandTotal > 0
        ? `The document prints a total of $${Number(rq.grandTotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}; Audee read 0 ${noun}, so none of its checks ran.`
        : null;
    return {
        title: 'Estimate not read — no audit was performed',
        lead,
        totalLine,
        closing: "This is not a clean result. It's a gap on our side, not a problem with your file — send it to support and we'll add this layout.",
    };
}

module.exports = { computeReadQuality, notReadCopy, countTmScheduleEntries, printedTotal, layoutIdentified };
